import {toRaw, watch} from 'vue';

import settings from '@/config/settings';

import type {IKeycloakUser} from '@/assets/keycloak/types';

import {keycloakAPI} from '@/assets/keycloak';

import NotificationWorker from '../notifications/notifications.worker.ts?sharedworker';

const channel = new BroadcastChannel('notifications');

let sharedWorker: SharedWorker | null = null;

let lastToken: string | null = null;
let lastUserInfo: IKeycloakUser | null = null;
let lastIsAuthenticated: boolean | null = null;

const initWorker = () => {
  try {
    if (typeof SharedWorker !== 'undefined') {
      sharedWorker = new NotificationWorker({
        name: 'notifications-worker',
      });

      sharedWorker.port.start();

      if (
        typeof Notification !== 'undefined' &&
        Notification.permission === 'default'
      ) {
        Notification.requestPermission().then((permission) => {
          console.log('Notification permission:', permission);
          sendConfigToWorker();
        });
      } else {
        sendConfigToWorker();
      }
    }
  } catch (error) {
    console.error('Failed to init shared worker:', error);
  }
};

const sendTokenToWorker = (token: string | null) => {
  if (token) {
    channel.postMessage({
      type: 'UPDATE_TOKEN',
      token: token,
      timestamp: Date.now(),
    });
  }
};

const sendUserInfoToWorker = (userInfo: IKeycloakUser) => {
  const plainUserInfo = JSON.parse(JSON.stringify(userInfo));
  channel.postMessage({
    type: 'UPDATE_USER_INFO',
    userInfo: plainUserInfo,
    timestamp: Date.now(),
  });
};

const sendAuthChangeToWorker = (isAuthenticated: boolean) => {
  channel.postMessage({
    type: 'AUTH_CHANGE',
    isAuthenticated: isAuthenticated,
    timestamp: Date.now(),
  });

  if (isAuthenticated) {
    sendConfigToWorker();
  }
};

const sendConfigToWorker = () => {
  channel.postMessage({
    type: 'UPDATE_CONFIG',
    config: {
      backendUrl: settings.SERVICES.BASE.URL,
      pollingInterval: settings.SERVICES.FRONT.POLLING_INTERVAL,
    },
    timestamp: Date.now(),
  });
};

const resendAllCredentials = () => {
  sendConfigToWorker();
  if (lastToken) sendTokenToWorker(lastToken);
  if (lastUserInfo) sendUserInfoToWorker(lastUserInfo);
  if (lastIsAuthenticated !== null) {
    sendAuthChangeToWorker(lastIsAuthenticated);
  }
};

channel.onmessage = (event) => {
  console.log('Message from worker:', event.data);

  if (event.data.type === 'WORKER_READY') {
    resendAllCredentials();
  }
};

export const initMainAppWorker = () => {
  initWorker();

  watch(
    () => keycloakAPI.getToken.value,
    (newToken) => {
      lastToken = newToken;
      sendTokenToWorker(newToken);
      if (newToken) {
        lastUserInfo = toRaw(keycloakAPI.getUserInfo.value);
        sendUserInfoToWorker(lastUserInfo);
      }
    },
    {immediate: true},
  );

  watch(
    () => keycloakAPI.getIsLoggedIn.value,
    (isLoggedIn) => {
      lastIsAuthenticated = isLoggedIn;
      sendAuthChangeToWorker(isLoggedIn);
    },
    {immediate: true},
  );

  return {
    getWorker: () => sharedWorker,
  };
};
